import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { GuestMe, JoinInfo } from '@karozik/shared';
import { api, ApiError, formatCountdown, Spinner, useNow, useParty } from '@karozik/ui';
import { JoinForm } from '../components/JoinForm';
import { Party } from '../components/Party';
import { Recap } from './Recap';

/** /j/:token — the QR code target: join, wait, party or recap depending on the clock. */
export function GuestEntry() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<JoinInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<JoinInfo>(`/api/join/${token}`)
      .then(setInfo)
      .catch((e: ApiError) => setError(e.message));
  }, [token]);
  useEffect(load, [load]);

  if (error) return <Centered emoji="🔗" title="Lien invalide" text={error} />;
  if (!info) return <Centered emoji={<Spinner className="h-8 w-8" />} title="" text="" />;
  if (info.event.status === 'upcoming') return <Upcoming info={info} onStart={load} />;
  if (info.event.status === 'ended') return <Recap token={token} />;
  if (!info.me) return <JoinForm token={token} info={info} onJoined={(me) => setInfo({ ...info, me })} />;
  return <GuestParty token={token} me={info.me} onLost={() => setInfo({ ...info, me: null })} />;
}

function GuestParty({ token, me, onLost }: { token: string; me: GuestMe; onLost(): void }) {
  const conn = useParty({ role: 'guest', joinToken: token });
  useEffect(() => {
    // Banned, or the cookie disappeared: back to the join form.
    if (conn.error === 'not-joined') onLost();
  }, [conn.error, onLost]);
  if (conn.state?.event.status === 'ended') return <Recap token={token} />;
  return <Party conn={conn} me={conn.me ?? me} search={{ joinToken: token }} variant="phone" />;
}

function Upcoming({ info, onStart }: { info: JoinInfo; onStart(): void }) {
  const now = useNow(1000);
  const left = info.event.startsAt - now;
  useEffect(() => {
    if (left <= 0) onStart();
  }, [left <= 0]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Centered
      emoji="⏳"
      title={info.event.name}
      text={
        <>
          La playlist ouvre dans
          <span className="font-display mt-2 block text-5xl font-bold text-gradient">{formatCountdown(left)}</span>
        </>
      }
    />
  );
}

export function Centered({ emoji, title, text }: { emoji: React.ReactNode; title: string; text: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="text-6xl">{emoji}</div>
      {title && <h1 className="font-display text-2xl font-bold">{title}</h1>}
      <div className="text-ink-dim">{text}</div>
    </main>
  );
}
